/**
 * GraphQL interface config types + helpers (ADR-0058) — the FE mirror of the backend
 * `hub.apps.graphql_exposure` model. A `kind="graphql"` app interface exposes a curated
 * list of RESOURCES (table/dataset/query) over ONE schema-wide `run_as_role` + DoS caps;
 * the SDL is derived server-side. Twin of `restEndpoints.ts`.
 */
import { uid } from "@/lib/uid";

export type GraphqlSource = "table" | "dataset" | "query";

export interface GraphqlResource {
  /** Stable editor id (not the GraphQL field name). */
  id: string;
  source: GraphqlSource;
  /** The table/dataset/query this resource exposes. `null` = an inert DRAFT (skipped
   *  server-side until a target is picked). */
  target_id: string | null;
  /** The GraphQL field/type name; `null` = DERIVED from the target's name server-side. */
  name?: string | null;
  /** Expose create/update/delete mutations (TABLES only). Default read-only; the role's
   *  write-guard still gates every write. */
  writable?: boolean;
  enabled: boolean;
}

export interface GraphqlConfig {
  /** The ONE role the whole schema runs under (RLS + field-mask at every resolver).
   *  `null` = operator-trust (unrestricted). */
  run_as_role_id?: string | null;
  /** DoS caps (ADR-0058 decision 3) — bounded server-side (depth ≤20, nodes ≤5000). */
  max_depth: number;
  max_nodes: number;
  resources: GraphqlResource[];
}

export const MAX_DEPTH_DEFAULT = 8;
export const MAX_NODES_DEFAULT = 500;

/** Parse the interface `config` JSONB into a typed `GraphqlConfig` (never throws — an
 *  absent/malformed field falls back to the default). */
export function parseGraphqlConfig(config: unknown): GraphqlConfig {
  const c = (config ?? {}) as Record<string, unknown>;
  const rawResources = Array.isArray(c.resources) ? (c.resources as GraphqlResource[]) : [];
  return {
    run_as_role_id: (c.run_as_role_id as string | null | undefined) ?? null,
    max_depth: typeof c.max_depth === "number" ? c.max_depth : MAX_DEPTH_DEFAULT,
    max_nodes: typeof c.max_nodes === "number" ? c.max_nodes : MAX_NODES_DEFAULT,
    resources: rawResources.map((r) => ({
      id: String(r.id),
      source: (r.source as GraphqlSource) ?? "table",
      target_id: (r.target_id as string | null) ?? null,
      name: r.name ?? null,
      writable: !!r.writable,
      enabled: r.enabled !== false,
    })),
  };
}

/** A fresh resource of the given source (a draft — no target yet). */
export function emptyResource(source: GraphqlSource = "table"): GraphqlResource {
  return { id: uid("g"), source, target_id: null, name: null, writable: false, enabled: true };
}
